
export interface RentReminder {
  tenantId: string;
  propertyId: string;
  propertyTitle: string;
  amount: number;
  dueDate: string;
  daysUntilDue: number;
}

export interface MaintenanceUpdate {
  requestId: string;
  tenantId: string;
  landlordId: string;
  propertyId: string;
  propertyTitle: string;
  status: 'submitted' | 'acknowledged' | 'in_progress' | 'scheduled' | 'completed' | 'cancelled';
  description: string;
  scheduledDate?: string;
  completedDate?: string;
  assignedTo?: string;
}

export interface ApplicationUpdate {
  applicationId: string;
  applicantId: string;
  landlordId: string;
  propertyId: string;
  propertyTitle: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'withdrawn';
  message?: string;
}

export interface PropertyViewNotification {
  propertyId: string;
  landlordId: string;
  propertyTitle: string;
  viewerName?: string;
  viewerEmail?: string;
  viewType: 'listing_view' | 'inquiry' | 'tour_request' | 'application_started';
  message: string;
  metadata?: {
    tourDate?: string;
    inquiryMessage?: string;
  };
}

class NotificationService {
  private static instance: NotificationService;
  private addNotification: any;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  initialize(addNotificationFn: any) {
    this.addNotification = addNotificationFn;
  }

  // Rent Payment Reminders
  sendRentReminder(reminder: RentReminder) {
    const { daysUntilDue, amount, propertyTitle, dueDate } = reminder;
    
    let title = '';
    let message = '';
    let priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium';

    if (daysUntilDue < 0) {
      // Overdue
      title = 'Rent Payment Overdue';
      message = `Your rent payment of GH₵${amount} for ${propertyTitle} was due ${Math.abs(daysUntilDue)} days ago.`;
      priority = 'urgent';
    } else if (daysUntilDue === 0) {
      // Due today
      title = 'Rent Payment Due Today';
      message = `Your rent payment of GH₵${amount} for ${propertyTitle} is due today.`;
      priority = 'high';
    } else if (daysUntilDue <= 3) {
      // Due in 1-3 days
      title = `Rent Payment Due in ${daysUntilDue} Day${daysUntilDue > 1 ? 's' : ''}`;
      message = `Your rent payment of GH₵${amount} for ${propertyTitle} is due on ${new Date(dueDate).toLocaleDateString()}.`;
      priority = 'high';
    } else if (daysUntilDue <= 7) {
      // Due in 4-7 days
      title = 'Rent Payment Reminder';
      message = `Your rent payment of GH₵${amount} for ${propertyTitle} is due in ${daysUntilDue} days.`;
      priority = 'medium';
    }

    this.addNotification({
      type: 'payment',
      title,
      message,
      priority,
      isRead: false,
      actionUrl: '/payments',
      actionLabel: 'Pay Now',
      relatedId: reminder.propertyId,
      metadata: {
        propertyTitle,
        amount,
        dueDate
      }
    });
  }

  // Maintenance Request Updates
  sendMaintenanceUpdate(update: MaintenanceUpdate) {
    const { status, propertyTitle, description, scheduledDate, assignedTo } = update;
    
    let title = '';
    let message = '';
    let priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium';
    let actionUrl = `/maintenance/${update.requestId}`;

    switch (status) {
      case 'acknowledged':
        title = 'Maintenance Request Acknowledged';
        message = `Your maintenance request for ${propertyTitle} has been acknowledged. We'll get back to you soon.`;
        priority = 'low';
        break;
      
      case 'in_progress':
        title = 'Maintenance Work In Progress';
        message = `Work has started on your maintenance request for ${propertyTitle}: ${description}`;
        priority = 'medium';
        break;
      
      case 'scheduled':
        title = 'Maintenance Work Scheduled';
        message = `Your maintenance request for ${propertyTitle} has been scheduled for ${scheduledDate ? new Date(scheduledDate).toLocaleDateString() : 'soon'}.`;
        priority = 'high';
        break;
      
      case 'completed':
        title = 'Maintenance Work Completed';
        message = `The maintenance work for ${propertyTitle} has been completed. Please let us know if you need any follow-up.`;
        priority = 'high';
        break;
      
      case 'cancelled':
        title = 'Maintenance Request Cancelled';
        message = `Your maintenance request for ${propertyTitle} has been cancelled.`;
        priority = 'medium';
        break;
    }

    // Send to tenant
    this.addNotification({
      type: 'maintenance',
      title,
      message,
      priority,
      isRead: false,
      actionUrl,
      actionLabel: 'View Details',
      relatedId: update.requestId,
      metadata: {
        propertyTitle,
        ...(assignedTo && { senderName: assignedTo })
      }
    });

    // Also send to landlord for status updates
    if (['submitted', 'in_progress', 'completed'].includes(status)) {
      this.addNotification({
        type: 'maintenance',
        title: `Maintenance Update - ${propertyTitle}`,
        message: `Maintenance request status changed to: ${status.replace('_', ' ')}`,
        priority: status === 'completed' ? 'low' : 'medium',
        isRead: false,
        actionUrl,
        actionLabel: 'Manage Request',
        relatedId: update.requestId,
        metadata: {
          propertyTitle
        }
      });
    }
  }

  // Application Status Changes
  sendApplicationUpdate(update: ApplicationUpdate) {
    const { status, propertyTitle, message: customMessage } = update;
    
    let title = '';
    let message = '';
    let priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium';
    let actionUrl = `/applications/${update.applicationId}`;

    switch (status) {
      case 'under_review':
        title = 'Application Under Review';
        message = `Your application for ${propertyTitle} is now under review. We'll contact you soon with an update.`;
        priority = 'medium';
        break;
      
      case 'approved':
        title = 'Application Approved! 🎉';
        message = `Congratulations! Your application for ${propertyTitle} has been approved. ${customMessage || 'We\'ll be in touch with next steps.'}`;
        priority = 'high';
        break;
      
      case 'rejected':
        title = 'Application Update';
        message = customMessage || `Thank you for your interest in ${propertyTitle}. Unfortunately, we've decided to move forward with another applicant.`;
        priority = 'medium';
        break;
      
      case 'withdrawn':
        title = 'Application Withdrawn';
        message = `Your application for ${propertyTitle} has been withdrawn as requested.`;
        priority = 'low';
        break;
    }

    // Send to applicant
    this.addNotification({
      type: 'application',
      title,
      message,
      priority,
      isRead: false,
      actionUrl,
      actionLabel: 'View Application',
      relatedId: update.applicationId,
      metadata: {
        propertyTitle
      }
    });

    // Send to landlord
    this.addNotification({
      type: 'application',
      title: `Application ${status.replace('_', ' ')} - ${propertyTitle}`,
      message: `Application status updated for ${propertyTitle}`,
      priority: 'low',
      isRead: false,
      actionUrl: `/dashboard/applications`,
      actionLabel: 'View Applications',
      relatedId: update.applicationId,
      metadata: {
        propertyTitle
      }
    });
  }

  // Property View Notifications
  sendPropertyViewNotification(viewNotification: PropertyViewNotification) {
    const { propertyTitle, viewType, message, viewerName } = viewNotification;
    
    let title = '';
    let priority: 'low' | 'medium' | 'high' | 'urgent' = 'low';
    let actionUrl = `/property/${viewNotification.propertyId}`;

    switch (viewType) {
      case 'listing_view':
        title = 'Property Viewed';
        priority = 'low';
        break;
      
      case 'inquiry':
        title = 'New Property Inquiry';
        priority = 'medium';
        actionUrl = `/messages`;
        break;
      
      case 'tour_request':
        title = 'Tour Request Received';
        priority = 'high';
        actionUrl = `/bookings`;
        break;
      
      case 'application_started':
        title = 'New Application Started';
        priority = 'high';
        actionUrl = `/dashboard/applications`;
        break;
    }

    this.addNotification({
      type: 'system',
      title,
      message: `${message} ${viewerName ? `by ${viewerName}` : ''}`,
      priority,
      isRead: false,
      actionUrl,
      actionLabel: 'View Details',
      relatedId: viewNotification.propertyId,
      metadata: {
        propertyTitle,
        ...(viewerName && { senderName: viewerName })
      }
    });
  }

  // Batch send rent reminders (typically run daily)
  sendBatchRentReminders(reminders: RentReminder[]) {
    reminders.forEach(reminder => {
      this.sendRentReminder(reminder);
    });
  }

  // Schedule recurring notifications
  scheduleRecurringNotifications() {
    // This would typically be handled by a backend service
    // For demo purposes, we'll simulate with setTimeout
    
    // Daily rent reminder check
    const checkRentReminders = () => {
      // Mock data - in real app, this would fetch from API
      const mockReminders: RentReminder[] = [
        {
          tenantId: 'tenant1',
          propertyId: 'prop1',
          propertyTitle: 'Sunset View Apartment',
          amount: 1200,
          dueDate: '2024-02-01T00:00:00Z',
          daysUntilDue: 3
        }
      ];

      this.sendBatchRentReminders(mockReminders);
    };

    // Check daily at 9 AM
    setInterval(checkRentReminders, 24 * 60 * 60 * 1000);
  }
}

export default NotificationService; 